import type { GetUserSubscriptionStateWithSessionIdResponse } from "@hopper-proto/hopper/subscription/bff/proto/v1/GetUserSubscriptionStateWithSessionIdResponse";
import { SubscriptionStatus as SubscriptionStatusProto } from "@hopper-proto/hopper/subscription/bff/proto/v1/SubscriptionStatus";

type SubscriptionStatusEnum =
  (typeof SubscriptionStatusProto)[keyof typeof SubscriptionStatusProto];

/** Subset of subscription BFF `response` used for entitlement checks (HTTP or gRPC). */
export type UserSubscriptionBffState = {
  status?: SubscriptionStatusProto | number | string;
  isSubscriptionActive?: ProtoBoolInput;
};

export type ProtoBoolInput = boolean | { value?: boolean } | null | undefined;

/**
 * Reads protobuf `google.protobuf.BoolValue` from JSON or gRPC-web shapes,
 * or a plain boolean if the BFF flattens the field.
 */
export function readProtoBoolValue(input: ProtoBoolInput): boolean | undefined {
  if (input === null || input === undefined) {
    return undefined;
  }
  if (typeof input === "boolean") {
    return input;
  }
  if (typeof input === "object" && "value" in input) {
    const v = (input as { value?: boolean }).value;
    return typeof v === "boolean" ? v : undefined;
  }
  return undefined;
}

export function parseProtoSubscriptionStatus(
  status?: SubscriptionStatusProto | number | string
): SubscriptionStatusEnum {
  let normalized: SubscriptionStatusProto | number | undefined = status as
    | SubscriptionStatusProto
    | number
    | undefined;

  if (typeof status === "string") {
    const numeric = Number(status);
    if (!Number.isNaN(numeric) && String(numeric) === status) {
      normalized = numeric;
    } else {
      const byName = (
        SubscriptionStatusProto as unknown as Record<string, unknown>
      )[status];
      if (typeof byName === "number") {
        normalized = byName;
      }
    }
  }

  switch (normalized) {
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_UNSPECIFIED:
    case 0:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_UNSPECIFIED;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_NON_SUBSCRIBED:
    case 1:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_NON_SUBSCRIBED;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID:
    case 2:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_FREE_TRIAL:
    case 3:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_FREE_TRIAL;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_TRIAL_CANCELED_ACTIVE:
    case 4:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_TRIAL_CANCELED_ACTIVE;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_TRIAL_CANCELED_INACTIVE:
    case 5:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_TRIAL_CANCELED_INACTIVE;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID_CHURNED_ACTIVE:
    case 6:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID_CHURNED_ACTIVE;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID_CHURNED_INACTIVE:
    case 7:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID_CHURNED_INACTIVE;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_INELIGIBLE:
    case 8:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_INELIGIBLE;
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID_CHURNED_BILLING:
    case 9:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID_CHURNED_BILLING;
    default:
      return SubscriptionStatusProto.SUBSCRIPTION_STATUS_UNSPECIFIED;
  }
}

/**
 * Normalizes `GetUserSubscriptionStateWithSessionId` for shared entitlement helpers
 * (same proto shape whether the call is gRPC or JSON-over-HTTP).
 */
export function userSubscriptionBffStateFromGrpc(
  message?: GetUserSubscriptionStateWithSessionIdResponse | null
): UserSubscriptionBffState {
  const r = message?.response;
  if (!r) {
    return {};
  }
  return {
    status: r.status,
    isSubscriptionActive: r.isSubscriptionActive,
  };
}

/**
 * Status-only fallback when `isSubscriptionActive` is absent from the response.
 * Does not treat `PAID_CHURNED_BILLING` as entitled; billing churn is not an active
 * subscription for perks—use `response.isSubscriptionActive` from the BFF when present.
 */
export function isActiveHopperProSubscription(
  status?: SubscriptionStatusEnum
): boolean {
  switch (status) {
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID:
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_FREE_TRIAL:
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_TRIAL_CANCELED_ACTIVE:
    case SubscriptionStatusProto.SUBSCRIPTION_STATUS_PAID_CHURNED_ACTIVE:
      return true;
    default:
      return false;
  }
}

/**
 * Uses `isSubscriptionActive` from the subscription BFF when set; otherwise falls back
 * to parsing `status` (see {@link isActiveHopperProSubscription}).
 */
export function isActiveHopperProFromUserSubscriptionState(
  state?: UserSubscriptionBffState | null
): boolean {
  if (!state) {
    return false;
  }
  const activeFlag = readProtoBoolValue(state.isSubscriptionActive);
  const parsedStatus = parseProtoSubscriptionStatus(state.status);
  const statusFallback = isActiveHopperProSubscription(parsedStatus);
  return activeFlag === true
    ? true
    : activeFlag === false
      ? false
      : statusFallback;
}

/**
 * @deprecated Prefer {@link isActiveHopperProFromUserSubscriptionState} with
 * `{ status, isSubscriptionActive }` from the BFF so billing-churn states respect
 * `isSubscriptionActive`.
 */
export function isActiveHopperProFromSubscriptionResponse(
  status?: SubscriptionStatusProto | number | string
): boolean {
  return isActiveHopperProFromUserSubscriptionState({ status });
}
